// Command palette 2.0 (Ctrl/Cmd+K): actions, every page, saved prompts and
// chats in one grouped, keyboard-driven list. Module scope; App passes the
// data in. Matching is word-based: every typed word must appear somewhere
// in the item's label or keywords.
import { useEffect, useMemo, useRef, useState } from "react";

const words = (q) => String(q || "").toLowerCase().split(/\s+/).filter(Boolean);
const matches = (ws, text) => ws.every((w) => text.includes(w));

export default function CommandPalette({ open, onClose, S, chats, onPickChat, prompts, onPickPrompt, actions, pages }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => {
    if (open) { setQ(""); setSel(0); setTimeout(() => inputRef.current?.focus(), 30); }
  }, [open]);

  const groups = useMemo(() => {
    const ws = words(q);
    const pick = (list, limit) => list.filter((it) => !ws.length || matches(ws, (it.label + " " + (it.keywords || "")).toLowerCase())).slice(0, limit);
    const out = [];
    const acts = pick(actions || [], ws.length ? 8 : 6);
    const pgs = pick((pages || []).map((p) => ({ ...p, keywords: (p.keywords || "") + " go open page" })), ws.length ? 8 : 0);
    const pr = pick((prompts || []).filter((p) => p && p.title).map((p) => ({ id: "prompt_" + p.id, label: p.title, keywords: "prompt", run: () => onPickPrompt && onPickPrompt(p) })), ws.length ? 6 : 3);
    const ch = pick((chats || []).filter((c) => c && c.title).map((c) => ({ id: "chat_" + c.id, label: c.title, keywords: "chat", run: () => onPickChat(c.id) })), 8);
    if (ws.length) {
      if (ch.length) out.push(["Chats", ch]);
      if (acts.length) out.push(["Actions", acts]);
      if (pgs.length) out.push(["Go to", pgs]);
      if (pr.length) out.push(["Prompts", pr]);
    } else {
      if (acts.length) out.push(["Actions", acts]);
      if (pr.length) out.push(["Prompts", pr]);
      if (ch.length) out.push(["Recent chats", ch.slice(0, 5)]);
    }
    return out;
  }, [q, actions, pages, prompts, chats]);
  const flat = groups.flatMap(([, items]) => items);
  const clampSel = Math.min(sel, Math.max(0, flat.length - 1));
  useEffect(() => {
    const el = listRef.current && listRef.current.querySelector(`[data-idx="${clampSel}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [clampSel]);
  if (!open) return null;
  const runItem = (it) => { if (!it) return; onClose(); it.run(); };

  let idx = -1;
  return (
    <div onClick={onClose} className="cf-modal-backdrop" style={{ position: "fixed", inset: 0, zIndex: 600, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "13vh" }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Command palette" className="cf-palette"
        style={{ width: "100%", maxWidth: 600, background: S.modalBg || S.surface, border: `1px solid ${S.border2}`, borderRadius: 16, boxShadow: S.shadowLg || "0 18px 50px rgba(15,23,42,0.30)", overflow: "hidden", margin: "0 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 16px", borderBottom: `1px solid ${S.border}` }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={S.text3} strokeWidth="2.2"><circle cx="11" cy="11" r="7" /><line x1="20" y1="20" x2="16.5" y2="16.5" /></svg>
          <input ref={inputRef} value={q} placeholder="Search chats, pages and actions…" aria-label="Command palette"
            onChange={(e) => { setQ(e.target.value); setSel(0); }}
            onKeyDown={(e) => {
              if (e.key === "Escape") { e.preventDefault(); onClose(); }
              else if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, flat.length - 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
              else if (e.key === "Enter") { e.preventDefault(); runItem(flat[clampSel]); }
            }}
            style={{ flex: 1, background: "transparent", border: "none", padding: "15px 0", color: S.text, fontSize: 15, outline: "none", fontFamily: "inherit", boxShadow: "none" }} />
          <kbd style={{ fontSize: 11, color: S.text3, border: `1px solid ${S.border2}`, borderRadius: 6, padding: "1px 6px", fontFamily: "inherit" }}>esc</kbd>
        </div>
        <div ref={listRef} style={{ maxHeight: "min(420px, 60vh)", overflowY: "auto", padding: 6 }}>
          {flat.length === 0 && <div style={{ padding: "22px 12px", textAlign: "center", color: S.text3, fontSize: 13 }}>No matches for “{q}”</div>}
          {groups.map(([title, items]) => (
            <div key={title}>
              <div style={{ padding: "8px 10px 4px", fontSize: 11, fontWeight: 600, color: S.text3 }}>{title}</div>
              {items.map((it) => {
                idx += 1;
                const i = idx;
                const on = i === clampSel;
                return (
                  <div key={it.id} data-idx={i} role="option" aria-selected={on} onClick={() => runItem(it)} onMouseMove={() => { if (sel !== i) setSel(i); }}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 9, cursor: "pointer", background: on ? S.accentDim : "transparent", color: on ? S.text : S.text2, fontSize: 13.5 }}>
                    <span style={{ width: 18, height: 18, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: on ? S.accent : S.text3 }}>{it.icon || <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>}</span>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.label}</span>
                    {it.hint && <span style={{ fontSize: 11, color: S.text3 }}>{it.hint}</span>}
                    {on && <span style={{ fontSize: 11, color: S.text3 }}>↵</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div style={{ padding: "8px 14px", borderTop: `1px solid ${S.border}`, fontSize: 11, color: S.text3, display: "flex", gap: 16 }}>
          <span>↑↓ navigate</span><span>↵ open</span><span>esc close</span>
        </div>
      </div>
    </div>
  );
}
